import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Mail, Settings2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ChallanFields } from "@/components/logistics/ChallanFields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/app/common";
import { EmailChips } from "@/components/sales/EmailChips";
import { useFetch } from "@/hooks/useFetch";
import { api, downloadFile, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

function EmailLog({ rows }) {
  if (!rows?.length) return <p className="text-xs text-slate-400" data-testid="dc-email-log-empty">Not emailed yet</p>;
  return rows.map((e) => (
    <div key={e.id} className="border-b border-slate-100 py-1 text-xs" data-testid="dc-email-log-row">
      <div className="flex items-center gap-2"><StatusBadge status={e.status} /><span className="text-slate-500">{fmtDateTime(e.sent_at)} · {e.sent_by?.name}</span></div>
      <div>To: {e.to.join(", ")}{e.cc?.length ? ` · CC: ${e.cc.join(", ")}` : ""}</div>
      {e.error && <div className="text-red-700">{e.error}</div>}
    </div>
  ));
}

export function DcEmailDialog({ base, onClose }) {
  return <DocEmailDialog url={`${base}/dc-email`} title="Email Delivery Challan" attachLabel="Send with PDF" onClose={onClose} />;
}

// Shared email dialog: GET url gives defaults (suggestions, subject, files); POST url sends
export function DocEmailDialog({ url, kind, title, attachLabel = "Send with attachments", onClose }) {
  const { data: c, reload } = useFetch(kind ? `${url}?kind=${kind}` : url);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (c && !f) setF({ to: c.suggestions[0] ? [c.suggestions[0].email] : [], cc: [], subject: c.subject, message: c.message, doc_ids: (c.files || []).map((x) => x.id) });
  }, [c, f]);
  const [confirm, setConfirm] = useState(false);
  const send = async () => {
    setConfirm(false);
    setBusy(true);
    try { await api.post(url, kind ? { ...f, kind } : f); toast.success("Email sent"); reload(); onClose(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const flip = (id) => setF({ ...f, doc_ids: f.doc_ids.includes(id) ? f.doc_ids.filter((x) => x !== id) : [...f.doc_ids, id] });
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dc-email-dialog">
        <DialogHeader><DialogTitle>{title} {c?.dc_no || ""}</DialogTitle></DialogHeader>
        {!f ? <div className="text-sm text-slate-500">Loading…</div> : <div className="space-y-3">
          {!c.configured && <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="dc-email-not-configured">Email sending is not set up yet — the files can still be downloaded.</p>}
          {c.files && <div data-testid="doc-email-files"><Label>Attach</Label>{c.files.map((x) => <label key={x.id} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={f.doc_ids.includes(x.id)} onChange={() => flip(x.id)} data-testid={`doc-email-file-${x.id}`} />{x.invoice_no ? `${x.invoice_no} · ` : ""}{x.name}</label>)}</div>}
          <div><Label>To *</Label><EmailChips value={f.to} onChange={(v) => setF({ ...f, to: v })} suggestions={c.suggestions} placeholder="Type an email and press Enter" testid="dc-email-to" /></div>
          <div><Label>CC</Label><EmailChips value={f.cc} onChange={(v) => setF({ ...f, cc: v })} suggestions={c.suggestions.filter((x) => !f.to.includes(x.email))} placeholder="Add people to copy" testid="dc-email-cc" /></div>
          <p className="text-[11px] text-slate-500">You are always copied{c.reply_to ? ` (${c.reply_to})` : ""}; replies come to you.</p>
          <div><Label>Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="dc-email-subject" /></div>
          <div><Label>Message</Label><Textarea rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="dc-email-message" /></div>
          <div><Label>Added below your message</Label><pre className="whitespace-pre-wrap rounded bg-slate-50 p-2 text-[11px] text-slate-600" data-testid="dc-email-preview">{c.preview}</pre></div>
          <div><Label>Sent before</Label><EmailLog rows={c.history} /></div>
        </div>}
        <DialogFooter><Button onClick={() => setConfirm(true)} disabled={!f || !f.to.length || busy || !c?.configured || (c.files && !f.doc_ids.length)} data-testid="dc-email-send"><Mail className="h-4 w-4 mr-1" />{busy ? "Sending…" : attachLabel}</Button></DialogFooter>
        <AlertDialog open={confirm} onOpenChange={setConfirm}>
          <AlertDialogContent data-testid="dc-email-confirm">
            <AlertDialogHeader><AlertDialogTitle>Send this email?</AlertDialogTitle>
              <AlertDialogDescription>A real email with the attachment(s) goes to: {f?.to.join(", ")}{f?.cc.length ? ` · CC ${f.cc.join(", ")}` : ""}. It cannot be recalled.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="dc-email-confirm-cancel">Go back</AlertDialogCancel>
              <AlertDialogAction onClick={send} data-testid="dc-email-confirm-send">Yes, send email</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}

const COPIES = [["all", "All 3 copies"], ["original", "Original for consignee"], ["duplicate", "Duplicate for transporter"], ["triplicate", "Triplicate for consigner"]];

function DetailsDialog({ base, doc, onClose, onSaved }) {
  const [f, setF] = useState({ warehouse_id: doc.warehouse_id || null, purpose: doc.purpose || "supply", special_handling: doc.special_handling || "" });
  const save = async () => {
    try { const r = await api.put(`${base}/challan-details`, f); toast.success("Challan details saved"); onSaved?.(r.data); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dc-details-dialog">
        <DialogHeader><DialogTitle>Challan details</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3"><ChallanFields value={f} onChange={setF} testid="dc-details" /></div>
        <p className="text-[11px] text-slate-500">Warehouse sets the consigner address and GSTIN; purpose decides whether GST is shown on the challan.</p>
        <DialogFooter><Button onClick={save} data-testid="dc-details-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Download / email / details for a Delivery Challan; base = /dispatches/{id} or /movements/{id}.
export function DcActions({ base, dcNo, canEmail, doc = {}, onChanged }) {
  const [open, setOpen] = useState(null);
  const [cur, setCur] = useState(doc);
  const download = async (copy) => {
    try { await downloadFile(`${base}/dc-pdf?copy=${copy}`, `Delivery_Challan_${dcNo}${copy === "all" ? "" : `_${copy}`}.pdf`); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid="dc-actions">
      <DropdownMenu>
        <DropdownMenuTrigger asChild><Button size="sm" variant="outline" data-testid="dc-download-btn"><Download className="h-4 w-4 mr-1" />Delivery Challan PDF</Button></DropdownMenuTrigger>
        <DropdownMenuContent>{COPIES.map(([k, l]) => <DropdownMenuItem key={k} onClick={() => download(k)} data-testid={`dc-download-${k}`}>{l}</DropdownMenuItem>)}</DropdownMenuContent>
      </DropdownMenu>
      {canEmail && <Button size="sm" variant="outline" onClick={() => setOpen("email")} data-testid="dc-email-btn"><Mail className="h-4 w-4 mr-1" />Email challan</Button>}
      {canEmail && <Button size="sm" variant="ghost" onClick={() => setOpen("details")} data-testid="dc-details-btn"><Settings2 className="h-4 w-4 mr-1" />Challan details</Button>}
      {open === "email" && <DcEmailDialog base={base} onClose={() => setOpen(null)} />}
      {open === "details" && <DetailsDialog base={base} doc={cur} onClose={() => setOpen(null)} onSaved={(r) => { setCur({ ...cur, ...r }); onChanged?.(); }} />}
    </div>
  );
}
